import React from 'react';
import { ReportData } from '../../types/interfaces';
import { useTranslation } from 'react-i18next';

interface CriticalityDataProps {
  summaryData: ReportData;
}

interface CriticalityDataItem {
  id: number;
  count: number;
  keys: {
    zero?: string; // Clave para cuando count es 0 (opcional)
    one: string;   // Clave para cuando count es 1
    other: string; // Clave para cuando count es mayor a 1
  };
  severity: string;
}

const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500 text-white';
    case 'high':
      return 'bg-orange-500 text-white';
    case 'medium':
      return 'bg-yellow-500 text-white';
    case 'low':
      return 'bg-green-300 text-white';
    case 'correct':
      return 'bg-green-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
};

export const getCriticalityData = (summaryData: ReportData): CriticalityDataItem[] => {
  const data: CriticalityDataItem[] = [
    {
      id: 1,
      count: summaryData.num_trabajadores,
      keys: {
        zero: 'criticality.no_workers',
        one: 'criticality.workers',
        other: 'criticality.workers_plural',
      },
      severity:
        summaryData.num_trabajadores > 5
          ? 'high'
          : summaryData.num_trabajadores > 0
          ? 'medium'
          : 'correct',
    },
    {
      id: 2,
      count: summaryData.num_ips,
      keys: {
        zero: 'criticality.no_ips',
        one: 'criticality.ips',
        other: 'criticality.ips_plural',
      },
      severity: summaryData.num_ips > 0 ? 'medium' : 'correct',
    },
    {
      id: 3,
      count: summaryData.num_emails,
      keys: {
        zero: 'criticality.no_emails',
        one: 'criticality.emails',
        other: 'criticality.emails_plural',
      },
      severity:
        summaryData.num_emails > 5
          ? 'high'
          : summaryData.num_emails > 0
          ? 'medium'
          : 'correct',
    },
    {
      id: 4,
      count: summaryData.num_filtraciones,
      keys: {
        zero: 'criticality.no_data_leaks',
        one: 'criticality.data_leak',
        other: 'criticality.data_leak_plural',
      },
      severity: summaryData.num_filtraciones > 0 ? 'critical' : 'correct',
    },
    {
      id: 5,
      count: summaryData.num_subdomains,
      keys: {
        zero: 'criticality.no_subdomains',
        one: 'criticality.subdomains',
        other: 'criticality.subdomains_plural',
      },
      severity: summaryData.num_subdomains > 0 ? 'medium' : 'correct',
    },
    {
      id: 6,
      count: summaryData.num_typosquatting,
      keys: {
        zero: 'criticality.no_typosquatting',
        one: 'criticality.typosquatting',
        other: 'criticality.typosquatting_plural',
      },
      severity: summaryData.num_typosquatting > 0 ? 'medium' : 'correct',
    },
    {
      id: 7,
      count: summaryData.num_dominios_spoofeables,
      keys: {
        zero: 'criticality.no_spoofable_domains',
        one: 'criticality.spoofable_domain',
        other: 'criticality.spoofable_domain_plural',
      },
      severity: summaryData.num_dominios_spoofeables > 0 ? 'critical' : 'correct',
    },
    {
      id: 8,
      count: summaryData.len_vulns,
      keys: {
        zero: 'criticality.no_vulnerabilities',
        one: 'criticality.vulnerabilities',
        other: 'criticality.vulnerabilities_plural',
      },
      severity:
        summaryData.len_vulns > 4
          ? 'high'
          : summaryData.len_vulns > 0
          ? 'medium'
          : 'correct',
    },
  ];

  // Datos adicionales si hay información de directorios
  if (summaryData.num_directories !== undefined) {
    const additionalData: CriticalityDataItem[] = [
      {
        id: 9,
        count: summaryData.num_technologies,
        keys: {
          zero: 'criticality.no_technologies',
          one: 'criticality.technologies',
          other: 'criticality.technologies_plural',
        },
        severity: summaryData.num_technologies > 0 ? 'low' : 'correct',
      },
      {
        id: 10,
        count: summaryData.num_directories,
        keys: {
          zero: 'criticality.no_directories',
          one: 'criticality.directories',
          other: 'criticality.directories_plural',
        },
        severity: summaryData.num_directories > 0 ? 'low' : 'correct',
      },
      {
        id: 11,
        count: summaryData.len_dir_sensibles,
        keys: {
          zero: 'criticality.no_sensitive_directories',
          one: 'criticality.sensitive_directories',
          other: 'criticality.sensitive_directories_plural',
        },
        severity: summaryData.len_dir_sensibles > 0 ? 'high' : 'correct',
      },
      {
        id: 12,
        count: summaryData.num_urls_without_waf,
        keys: {
          zero: 'criticality.no_urls_without_waf',
          one: 'criticality.urls_without_waf',
          other: 'criticality.urls_without_waf_plural',
        },
        severity: summaryData.num_urls_without_waf > 0 ? 'high' : 'correct',
      },
    ];
    data.push(...additionalData);
  }

  // Ordenar según la criticidad (de mayor a menor)
  const severityOrder: Record<string, number> = {
    critical: 1,
    high: 2,
    medium: 3,
    low: 4,
    correct: 5,
  };
  data.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return data;
};

export const countVulnerabilitiesBySeverity = (summaryData: ReportData) => {
  const data = getCriticalityData(summaryData);
  return {
    critical: data.filter(item => item.severity === 'critical').length,
    high: data.filter(item => item.severity === 'high').length,
    medium: data.filter(item => item.severity === 'medium').length,
    low: data.filter(item => item.severity === 'low').length,
  };
};

const CriticalityData: React.FC<CriticalityDataProps> = ({ summaryData }) => {
  const { t } = useTranslation();
  const data = getCriticalityData(summaryData);

  // Función para seleccionar manualmente la clave a mostrar según el valor de count
  const renderDescription = (item: CriticalityDataItem) => {
    const count = item.count;
    if (count === 0 && item.keys.zero) {
      return t(item.keys.zero);
    } else if (count === 1) {
      return t(item.keys.one, { count });
    } else {
      return t(item.keys.other, { count });
    }
  };

  return (
    <div className="divide-y divide-gray-200">
      {data.length > 0 ? (
        data.map(item => (
          <div
            key={item.id}
            className="flex items-center py-2 hover:bg-gray-100 rounded transition-colors duration-200"
          >
            <div className="w-24 flex-shrink-0 flex justify-center">
              <span
                className={`px-4 py-1 text-sm leading-5 font-semibold rounded-full ${getSeverityColor(
                  item.severity
                )} text-center`}
              >
                {t(`severity.${item.severity}`)}
              </span>
            </div>
            <div className="ml-4 text-gray-700 flex-1">{renderDescription(item)}</div>
          </div>
        ))
      ) : (
        <p className="py-2 text-gray-500">{t('criticality.noData')}</p>
      )}
    </div>
  );
};

export default CriticalityData;
